/**
 * Community Group Settings Page
 * /community/groups/:groupId/settings - 모임 설정 페이지
 */

import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Users, Shield, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { australianStates, getSuburbsByState } from "@shared/suburbs";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GroupMembersTab } from "@/components/community/GroupMembersTab";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const formSchema = z.object({
  title: z.string().min(3, "모임명은 최소 3자 이상이어야 합니다").max(100),
  description: z.string().min(10, "설명은 최소 10자 이상이어야 합니다").max(2000),
  categoryId: z.string().min(1, "카테고리를 선택해주세요"),
  city: z.string().min(1, "도시를 입력해주세요"),
  state: z.string().min(2, "주를 선택해주세요"),
  suburb: z.string().optional(),
  isJoinApprovalRequired: z.boolean().default(false),
  maxMembers: z.number().int().positive().optional().or(z.literal("")),
  coverImageUrl: z.string().url().optional().or(z.literal("")),
});

type FormData = z.infer<typeof formSchema>;

interface GroupCategory {
  id: number;
  slug: string;
  name: string;
  name_ko: string;
  icon: string;
}

interface Group {
  id: number;
  title: string;
  slug: string;
  description: string;
  category_id: number;
  category_name: string;
  category_name_ko: string;
  category_icon: string;
  city: string;
  state: string;
  suburb: string | null;
  cover_image_url: string | null;
  is_join_approval_required: number;
  max_members: number | null;
  owner_uid: string;
  status: string;
}

export default function GroupSettingsPage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ groupId: string }>("/community/groups/:groupId/settings");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("info");

  const groupId = params?.groupId ? parseInt(params.groupId) : null;

  // 그룹 상세 조회
  const { data: group, isLoading: groupLoading } = useQuery<Group>({
    queryKey: ["/api/community/groups", groupId],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}`);
      if (!response.ok) throw new Error("Failed to fetch group");
      return response.json();
    },
    enabled: !!groupId,
  });

  // 카테고리 목록 조회
  const { data: categoriesData } = useQuery<{ categories: GroupCategory[] }>({
    queryKey: ["/api/community/categories"],
    queryFn: async () => {
      const response = await fetch("/api/community/categories");
      if (!response.ok) throw new Error("Failed to fetch categories");
      return response.json();
    },
  });

  const categories = categoriesData?.categories || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isJoinApprovalRequired: false,
    },
  });

  // 그룹 데이터가 로드되면 폼에 설정
  useQuery({
    queryKey: ["group-form-data", group?.id],
    queryFn: () => {
      if (group) {
        reset({
          title: group.title,
          description: group.description,
          categoryId: group.category_id.toString(),
          city: group.city,
          state: group.state,
          suburb: group.suburb || "",
          isJoinApprovalRequired: group.is_join_approval_required === 1,
          maxMembers: group.max_members || undefined,
          coverImageUrl: group.cover_image_url || "",
        });
      }
      return null;
    },
    enabled: !!group,
  });

  const selectedState = watch("state");
  const availableCities = selectedState ? getSuburbsByState(selectedState) : [];

  // 권한 확인
  const isOwner = user && group && user.uid === group.owner_uid;

  // 모임 정보 수정
  const updateGroupMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");
      if (!isOwner) throw new Error("모임 주최자만 수정할 수 있습니다");

      const token = await user.getIdToken();
      const requestBody = {
        ...data,
        categoryId: parseInt(data.categoryId),
        maxMembers: data.maxMembers === "" ? null : data.maxMembers,
      };

      const response = await fetch(`/api/community/groups/${groupId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "모임 수정에 실패했습니다");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId] });
      toast({
        title: "모임 정보가 수정되었습니다",
        description: "변경사항이 저장되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 모임 삭제
  const deleteGroupMutation = useMutation({
    mutationFn: async () => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");
      if (!isOwner) throw new Error("모임 주최자만 삭제할 수 있습니다");

      const token = await user.getIdToken();
      const response = await fetch(`/api/community/groups/${groupId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "모임 삭제에 실패했습니다");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups"] });
      toast({
        title: "모임이 삭제되었습니다",
      });
      navigate("/community/groups");
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    updateGroupMutation.mutate(data);
  };

  if (!match || !params || !groupId) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">유효하지 않은 모임 ID입니다</p>
            <Button className="mt-4" onClick={() => navigate("/community/groups")}>
              목록으로
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (groupLoading) {
    return (
      <PageContainer size="read">
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </PageContainer>
    );
  }

  if (!group) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">모임을 찾을 수 없습니다</p>
            <Button className="mt-4" onClick={() => navigate("/community/groups")}>
              목록으로
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (!isOwner) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">모임 주최자만 설정을 변경할 수 있습니다</p>
            <Button className="mt-4" onClick={() => navigate(`/community/groups/${groupId}`)}>
              모임으로 돌아가기
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <>
      <Seo
        title={`${group.title} - 모임 설정`}
        description={group.description}
        path={`/community/groups/${groupId}/settings`}
      />
      <PageContainer size="read">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/community/groups/${groupId}`)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">모임 설정</h1>
              <p className="text-muted-foreground">{group.title}</p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="info">기본 정보</TabsTrigger>
              <TabsTrigger value="members">멤버 관리</TabsTrigger>
              <TabsTrigger value="danger">위험 설정</TabsTrigger>
            </TabsList>

            {/* 기본 정보 탭 */}
            <TabsContent value="info">
              <Card>
                <CardHeader>
                  <CardTitle>기본 정보</CardTitle>
                  <CardDescription>모임의 기본 정보를 수정할 수 있습니다</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* 모임명 */}
                    <div className="space-y-2">
                      <Label htmlFor="title">모임명 *</Label>
                      <Input
                        id="title"
                        {...register("title")}
                        placeholder="예: 시드니 한식 모임"
                      />
                      {errors.title && (
                        <p className="text-sm text-destructive">{errors.title.message}</p>
                      )}
                    </div>

                    {/* 설명 */}
                    <div className="space-y-2">
                      <Label htmlFor="description">설명 *</Label>
                      <Textarea
                        id="description"
                        {...register("description")}
                        placeholder="모임에 대한 설명을 입력해주세요"
                        rows={6}
                      />
                      {errors.description && (
                        <p className="text-sm text-destructive">{errors.description.message}</p>
                      )}
                    </div>

                    {/* 카테고리 */}
                    <div className="space-y-2">
                      <Label htmlFor="categoryId">카테고리 *</Label>
                      <Select
                        value={watch("categoryId") || ""}
                        onValueChange={(value) => setValue("categoryId", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="카테고리를 선택해주세요" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id.toString()}>
                              {category.name_ko || category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.categoryId && (
                        <p className="text-sm text-destructive">{errors.categoryId.message}</p>
                      )}
                    </div>

                    {/* 주 */}
                    <div className="space-y-2">
                      <Label htmlFor="state">주 *</Label>
                      <Select
                        value={watch("state") || ""}
                        onValueChange={(value) => {
                          setValue("state", value);
                          setValue("city", "");
                          setValue("suburb", "");
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="주를 선택해주세요" />
                        </SelectTrigger>
                        <SelectContent>
                          {australianStates.map((state) => (
                            <SelectItem key={state.id} value={state.id}>
                              {state.nameKo || state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.state && (
                        <p className="text-sm text-destructive">{errors.state.message}</p>
                      )}
                    </div>

                    {/* 도시 */}
                    {selectedState && availableCities.length > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="city">도시 *</Label>
                        <Select
                          value={watch("city") || ""}
                          onValueChange={(value) => setValue("city", value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="도시를 선택해주세요" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableCities.map((city) => (
                              <SelectItem key={city.id} value={city.id}>
                                {city.nameKo || city.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.city && (
                          <p className="text-sm text-destructive">{errors.city.message}</p>
                        )}
                      </div>
                    )}

                    {/* 지역 */}
                    <div className="space-y-2">
                      <Label htmlFor="suburb">지역 (선택)</Label>
                      <Input
                        id="suburb"
                        {...register("suburb")}
                        placeholder="예: Strathfield"
                      />
                    </div>

                    {/* 가입 승인 필요 */}
                    <div className="flex items-center justify-between space-x-2">
                      <div className="space-y-0.5">
                        <Label htmlFor="isJoinApprovalRequired">가입 승인 필요</Label>
                        <p className="text-sm text-muted-foreground">
                          체크하면 가입 신청 시 승인이 필요합니다
                        </p>
                      </div>
                      <Switch
                        id="isJoinApprovalRequired"
                        checked={watch("isJoinApprovalRequired")}
                        onCheckedChange={(checked) => setValue("isJoinApprovalRequired", checked)}
                      />
                    </div>

                    {/* 최대 인원 */}
                    <div className="space-y-2">
                      <Label htmlFor="maxMembers">최대 인원 (선택)</Label>
                      <Input
                        id="maxMembers"
                        type="number"
                        {...register("maxMembers", {
                          setValueAs: (v) => (v === "" ? "" : parseInt(v, 10)),
                        })}
                        placeholder="예: 50"
                        min={1}
                      />
                      <p className="text-sm text-muted-foreground">
                        비워두면 인원 제한이 없습니다
                      </p>
                    </div>

                    {/* 대표 이미지 */}
                    <div className="space-y-2">
                      <Label htmlFor="coverImageUrl">대표 이미지 URL (선택)</Label>
                      <Input
                        id="coverImageUrl"
                        {...register("coverImageUrl")}
                        placeholder="https://..."
                        type="url"
                      />
                    </div>

                    {/* 저장 버튼 */}
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/community/groups/${groupId}`)}
                      >
                        취소
                      </Button>
                      <Button type="submit" disabled={updateGroupMutation.isPending}>
                        <Save className="w-4 h-4 mr-2" />
                        {updateGroupMutation.isPending ? "저장 중..." : "저장"}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* 멤버 관리 탭 */}
            <TabsContent value="members">
              <Card>
                <CardHeader>
                  <CardTitle>멤버 관리</CardTitle>
                  <CardDescription>모임 멤버를 승인하거나 관리할 수 있습니다</CardDescription>
                </CardHeader>
                <CardContent>
                  <GroupMembersTab groupId={groupId} isOwner={isOwner} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* 위험 설정 탭 */}
            <TabsContent value="danger">
              <Card>
                <CardHeader>
                  <CardTitle className="text-destructive">위험 설정</CardTitle>
                  <CardDescription>
                    모임을 삭제하면 복구할 수 없습니다. 신중하게 결정해주세요.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive">
                        <Trash2 className="w-4 h-4 mr-2" />
                        모임 삭제
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>모임을 삭제하시겠습니까?</AlertDialogTitle>
                        <AlertDialogDescription>
                          이 작업은 되돌릴 수 없습니다. 모임과 관련된 모든 데이터(일정, 후기, 사진 등)가
                          영구적으로 삭제됩니다.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>취소</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteGroupMutation.mutate()}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {deleteGroupMutation.isPending ? "삭제 중..." : "삭제"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </PageContainer>
    </>
  );
}
